import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { Nullable } from 'corgi-types';

import type { Color } from '../theme/colors/types';
import { useIsObserved } from './intersection-observer';

export const useLoadingImageBackground = (
  isLoaded: boolean,
  imageRef?: MutableRefObject<Nullable<HTMLImageElement>>
) => {
  const [backgroundColor, setBackgroundColor] = useState<Color | undefined>(
    isLoaded ? undefined : 'sys.neutral.background.weak'
  );

  const onImageLoad = useCallback(() => {
    setBackgroundColor('transparent');
  }, []);

  useEffect(() => {
    // handle edge cases of image being loaded before getting the load event attached
    if (imageRef?.current?.complete) {
      onImageLoad();
    } else {
      imageRef?.current?.addEventListener('load', onImageLoad);
    }

    return () => {
      imageRef?.current?.removeEventListener('load', onImageLoad);
    };
  }, []);

  return { backgroundColor, onImageLoad };
};

export const useIsImageLoaded = (isOutOfViewport: boolean) => {
  const [ref, isObserved] = useIsObserved();
  const [isLoaded, setIsLoaded] = useState(isObserved || !isOutOfViewport);

  useEffect(() => {
    if (HTMLImageElement.prototype.hasOwnProperty('loading') || isObserved) {
      setIsLoaded(true);
    }
  }, [isObserved]);

  return { ref, isLoaded };
};
